import React, { useState, useEffect, useRef } from "react";
import { vendorAPI, adminAPI, usersAPI, gmailAPI, formatError } from "../utils/api";
import { useKitchen } from "../contexts/KitchenContext";
import { useAuth } from "../contexts/AuthContext";
import { fmtDate } from "../utils/dateUtils";
import { Plus, Edit, Trash2, X, Search, Star, Mail, FileText, BarChart3,
         ChevronDown, ChevronRight, Download, Check, AlertTriangle, Loader,
         RefreshCw, Send, Inbox, CheckCircle, XCircle, ExternalLink, Award } from "lucide-react";
import { exportCSV } from "../utils/csvUtils";
import { useConfirm } from "../hooks/useConfirm";

// ─── SUPPLIER SCORECARD ───────────────────────────────────────────────────────
function SupplierScorecard() {
  const [scorecards, setScorecards] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => { fetchScorecard(); }, []);

  const fetchScorecard = async () => {
    setLoading(true);
    try {
      const r = await vendorAPI.getScorecard();
      setScorecards(r.data || []);
    } catch (e) { if (process.env.NODE_ENV === 'development') console.error('Fetch error:', e); }
    setLoading(false);
  };

  const GRADE_COLORS = { A: "bg-[#EDF2EE] text-[#637C64]", B: "bg-blue-50 text-blue-700", C: "bg-[#FDF4EB] text-[#D98032]", D: "bg-[#FBEAE7] text-[#C2624E]" };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-bold text-[#3A322C]">Supplier Performance Scorecard</h2>
          <p className="text-sm text-[#8A7E73] mt-0.5">Score based on fulfillment rate (50%), weight accuracy (40%), complaints (10%)</p>
        </div>
        <button onClick={fetchScorecard} disabled={loading} className="flex items-center gap-1.5 text-sm text-[#8A7E73] hover:text-[#5C5147]">
          <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>
      {scorecards.length === 0 && !loading && (
        <div className="text-center py-16 bg-white border border-[#E5E1DB] rounded-2xl text-[#8A7E73]">
          <Award size={40} className="mx-auto mb-3 opacity-20" />
          <p>No vendor data yet. Add vendors and create purchase orders to generate scorecards.</p>
        </div>
      )}
      {loading && <div className="text-center py-10"><Loader size={24} className="animate-spin text-[#E8956D] mx-auto" /></div>}
      {scorecards.length > 0 && (
        <div className="bg-white border border-[#E5E1DB] rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F4F3F0]">
                <tr>{["Grade","Vendor","Category","Orders","Spend","Fulfillment","Weight Accuracy","Pending >30d","Score"].map(h => (
                  <th key={h} className="text-left px-4 py-3 text-xs font-bold uppercase text-[#5C5147] whitespace-nowrap">{h}</th>
                ))}</tr>
              </thead>
              <tbody className="divide-y divide-[#F4F3F0]">
                {scorecards.map(s => (
                  <tr key={s.id} className="hover:bg-[#FAFAF8]">
                    <td className="px-4 py-3">
                      <span className={`text-sm font-extrabold px-2 py-1 rounded-lg ${GRADE_COLORS[s.grade] || GRADE_COLORS.C}`}>{s.grade}</span>
                    </td>
                    <td className="px-4 py-3">
                      <p className="font-bold text-[#3A322C]">{s.vendor_name}</p>
                      {s.is_preferred && <span className="text-[10px] text-[#637C64] font-bold">★ Preferred</span>}
                    </td>
                    <td className="px-4 py-3 text-xs text-[#8A7E73] capitalize">{s.category || "—"}</td>
                    <td className="px-4 py-3 font-bold text-[#3A322C]">{s.total_orders}</td>
                    <td className="px-4 py-3 font-bold text-[#E8956D]">₹{s.total_spend?.toLocaleString()}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-bold ${s.fulfillment_rate >= 90 ? "text-[#637C64]" : s.fulfillment_rate >= 70 ? "text-[#D98032]" : "text-[#C2624E]"}`}>
                        {s.fulfillment_rate}%
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-bold ${s.weight_accuracy_rate >= 95 ? "text-[#637C64]" : "text-[#D98032]"}`}>
                        {s.delivery_count > 0 ? `${s.weight_accuracy_rate}%` : "—"}
                      </span>
                      {s.weight_discrepancies > 0 && <p className="text-[10px] text-[#C2624E]">{s.weight_discrepancies} discrepancy(ies)</p>}
                    </td>
                    <td className="px-4 py-3">
                      {s.pending_over_30_days > 0 ? (
                        <span className="text-xs font-bold text-[#C2624E]">{s.pending_over_30_days}</span>
                      ) : <span className="text-xs text-[#C4BAAF]">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-20 bg-[#F4F3F0] rounded-full h-2">
                          <div className={`h-2 rounded-full ${s.score >= 85 ? "bg-[#637C64]" : s.score >= 70 ? "bg-blue-500" : s.score >= 50 ? "bg-[#D98032]" : "bg-[#C2624E]"}`}
                            style={{ width: `${s.score}%` }} />
                        </div>
                        <span className="font-bold text-[#3A322C] text-sm">{s.score}</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}




const CATEGORIES = ["vegetables", "groceries", "spices", "oils", "general", "packaging", "dairy"];
const TABS = [
  { id: "vendors",    label: "Vendors",            icon: FileText,  subKey: "purchase_vendors"   },
  { id: "contracts",  label: "Rate Contracts",     icon: BarChart3, subKey: "purchase_contracts" },
  { id: "quotes",     label: "Quote Requests",     icon: Mail,      subKey: "purchase_quotes"    },
  { id: "comparison", label: "Quote Comparison",   icon: BarChart3, subKey: "purchase_quotes"    },
  { id: "scorecard",  label: "Supplier Scorecard", icon: BarChart3, subKey: "purchase_scorecard" },
  { id: "gmail",      label: "Gmail",              icon: Mail,      subKey: "purchase_gmail"     },
];

function Modal({ title, onClose, children, wide = false }) {
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className={`bg-white rounded-2xl shadow-xl w-full ${wide ? "max-w-3xl" : "max-w-lg"} max-h-[92vh] overflow-y-auto animate-fade-in-up`}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E1DB] sticky top-0 bg-white">
          <h2 className="font-bold text-lg font-[Manrope] text-[#3A322C]">{title}</h2>
          <button onClick={onClose}><X size={20} /></button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

function StarRating({ value, onChange }) {
  return (
    <div className="flex gap-1">
      {[1,2,3,4,5].map(n => (
        <button key={n} type="button" onClick={() => onChange && onChange(n)}>
          <Star size={16} className={n <= value ? "fill-[#E8956D] text-[#E8956D]" : "text-[#C4BAAF]"} />
        </button>
      ))}
    </div>
  );
}

export default function VendorManagement() {
  const { confirm, ConfirmModal } = useConfirm();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("vendors");
  // Fallback: if active tab is hidden by permissions, switch to first visible tab
  useEffect(() => {
    const visible = TABS.filter(t => !t.subKey || user?.is_admin || user?.permissions?.[`sub_${t.subKey}_view`] !== false);
    if (visible.length > 0 && !visible.find(t => t.id === activeTab)) setActiveTab(visible[0].id);
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps
  const [vendors, setVendors] = useState([]);
  const [masterItems, setMasterItems] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [quoteRequests, setQuoteRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [search, setSearch] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [quoteResponseForm, setQuoteResponseForm] = useState({ vendor_id: "", items: [] });
  const [selectedVendors, setSelectedVendors] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);

  // Gmail state
  const [gmailStatus, setGmailStatus] = useState({ connected: false, email: null });
  const [gmailLoading, setGmailLoading] = useState(false);
  const [gmailMsg, setGmailMsg] = useState("");
  const [pendingQuotes, setPendingQuotes] = useState([]);
  const [pendingFilter, setPendingFilter] = useState("pending_review");
  const [sendRFQModal, setSendRFQModal] = useState(null);
  const [rfqMessage, setRfqMessage] = useState("");
  const [editingParsed, setEditingParsed] = useState(null);

  useEffect(() => {
    adminAPI.getMasterItems().then(r => setMasterItems(r.data || [])).catch(
      e => { if (process.env.NODE_ENV !== "production") console.warn("[fetch]", e); }
    );
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { fetchTab(); }, [activeTab, search, filterCategory]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load Gmail status on mount + when gmail tab is active
  useEffect(() => {
    gmailAPI.getStatus().then(r => setGmailStatus(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    if (activeTab === "gmail") {
      gmailAPI.getStatus().then(r => setGmailStatus(r.data)).catch(() => {});
      fetchPendingQuotes();
    }
  }, [activeTab]);

  // Check URL params for Gmail callback result
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("gmail_connected")) {
      const email = params.get("gmail_email") || "";
      setGmailStatus({ connected: true, email });
      setGmailMsg(`Gmail connected: ${email}`);
      setActiveTabAndClear("gmail");
      window.history.replaceState({}, "", window.location.pathname);
    }
    if (params.get("gmail_error")) {
      setGmailMsg("Gmail connection failed. Please try again.");
      setActiveTabAndClear("gmail");
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  const fetchTab = async () => {
    setLoading(true);
    try {
      if (activeTab === "vendors") {
        const params = {};
        if (filterCategory) params.category = filterCategory;
        if (search) params.search = search;
        const r = await vendorAPI.getVendors(params);
        setVendors(r.data || []);
      } else if (activeTab === "contracts") {
        const r = await vendorAPI.getRateContracts({ active_only: false });
        setContracts(r.data || []);
      } else if (activeTab === "quotes" || activeTab === "comparison") {
        const r = await vendorAPI.getQuoteRequests();
        setQuoteRequests(r.data || []);
      } else if (activeTab === "gmail") {
        const r = await vendorAPI.getQuoteRequests();
        setQuoteRequests(r.data || []);
      }
    } catch (e) { if (process.env.NODE_ENV === 'development') console.error('Fetch error:', e); }
    setLoading(false);
  };

  const handleSaveVendor = async () => {
    setError("");
    try {
      if (modal.mode === "create") await vendorAPI.createVendor(form);
      else await vendorAPI.updateVendor(modal.id, form);
      setModal(null); fetchTab();
      setSuccess("Saved!"); setTimeout(() => setSuccess(""), 2000);
    } catch (e) { setError(formatError(e)); }
  };

  const handleSaveContract = async () => {
    setError("");
    try {
      if (modal.mode === "create") await vendorAPI.createRateContract(form);
      else await vendorAPI.updateRateContract(modal.id, form);
      setModal(null); fetchTab();
    } catch (e) { setError(formatError(e)); }
  };

  const handleCreateQuoteRequest = async () => {
    setError("");
    try {
      await vendorAPI.createQuoteRequest({
        item_ids: selectedItems,
        vendor_ids: selectedVendors,
        kitchen_id: null,
        message: form.message || "",
      });
      setModal(null); setSelectedItems([]); setSelectedVendors([]);
      fetchTab(); setSuccess("Quote request created!"); setTimeout(() => setSuccess(""), 2000);
    } catch (e) { setError(formatError(e)); }
  };

  const handleAddResponse = async () => {
    setError("");
    try {
      await vendorAPI.addQuoteResponse({
        quote_request_id: selectedRequest.id,
        vendor_id: quoteResponseForm.vendor_id,
        items: quoteResponseForm.items,
        notes: quoteResponseForm.notes || ""
      });
      setModal(null); fetchTab();
      setSuccess("Quote response recorded!"); setTimeout(() => setSuccess(""), 2000);
    } catch (e) { setError(formatError(e)); }
  };

  const loadComparison = async (req) => {
    setSelectedRequest(req);
    try {
      const r = await vendorAPI.getQuoteComparison(req.id);
      setComparison(r.data);
    } catch (e) { if (process.env.NODE_ENV === 'development') console.error('Fetch error:', e); }
  };

  const setActiveTabAndClear = (tab) => { setActiveTab(tab); };

  const fetchPendingQuotes = async () => {
    try {
      const r = await gmailAPI.getPendingQuotes();
      setPendingQuotes(r.data || []);
    } catch (e) { if (process.env.NODE_ENV === 'development') console.error('Fetch error:', e); }
  };

  const handleGmailConnect = async () => {
    setGmailLoading(true);
    try {
      const r = await gmailAPI.connect();
      const authUrl = r.data?.auth_url;
      if (authUrl) window.location.href = authUrl;
    } catch (e) {
      setGmailMsg("Failed to start Gmail connection.");
    }
    setGmailLoading(false);
  };

  const handleGmailDisconnect = async () => {
    if (!await confirm("Disconnect Gmail account?")) return;
    await gmailAPI.disconnect();
    setGmailStatus({ connected: false, email: null });
    setGmailMsg("Gmail disconnected.");
  };

  const handleSendRFQ = async (req) => {
    setGmailLoading(true);
    setGmailMsg("");
    try {
      const r = await gmailAPI.sendRFQ({ quote_request_id: req.id, custom_message: rfqMessage });
      const { sent, failed } = r.data;
      setGmailMsg(`Sent to ${sent.length} vendor(s).${failed.length > 0 ? ` Failed: ${failed.map(f => f.vendor).join(", ")}` : ""}`);
      setSendRFQModal(null);
      setRfqMessage("");
      fetchTab();
    } catch (e) { setGmailMsg(formatError(e)); }
    setGmailLoading(false);
  };

  const handleFetchReplies = async () => {
    setGmailLoading(true);
    setGmailMsg("");
    try {
      const r = await gmailAPI.fetchReplies();
      const { new_parsed, fetched } = r.data;
      setGmailMsg(`Checked ${fetched} email(s). ${new_parsed} new vendor quote(s) parsed.`);
      fetchPendingQuotes();
    } catch (e) { setGmailMsg(formatError(e)); }
    setGmailLoading(false);
  };

  const handleApproveQuote = async (pending) => {
    try {
      await gmailAPI.approveQuote(pending.id, { pending_id: pending.id, quote_request_id: pending.matched_quote_request_id || "" });
      setGmailMsg("Quote approved and added to comparison!");
      fetchPendingQuotes();
    } catch (e) { setGmailMsg(formatError(e)); }
  };

  const handleRejectQuote = async (pending) => {
    if (!await confirm("Reject this parsed quote?")) return;
    await gmailAPI.rejectQuote(pending.id);
    fetchPendingQuotes();
  };

  const gmailPlaceholder = (
    <div className="bg-gradient-to-r from-[#4285F4]/5 to-[#34A853]/5 border border-[#4285F4]/20 rounded-xl p-5 flex items-start gap-4 mb-4">
      <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center shadow-sm flex-shrink-0">
        <Mail size={20} className="text-[#4285F4]" />
      </div>
      <div className="flex-1">
        <p className="font-bold text-[#3A322C]">Gmail Auto-Quote — Coming Tomorrow</p>
        <p className="text-sm text-[#8A7E73] mt-1">Once Gmail is connected, the AI will automatically send quote request emails to selected vendors and read their replies to populate the comparison table — no manual entry needed.</p>
        <div className="flex flex-wrap gap-2 mt-3">
          {["Auto-send RFQ emails", "AI reads vendor replies", "Auto-populate quotes", "Track response rate"].map(f => (
            <span key={f} className="text-xs bg-[#4285F4]/10 text-[#4285F4] px-2 py-0.5 rounded-full">{f}</span>
          ))}
        </div>
      </div>
      <span className="text-xs bg-[#FDF4EB] text-[#D98032] px-3 py-1 rounded-full font-bold flex-shrink-0">Pending</span>
    </div>
  );

  const filteredContracts = contracts.filter(c =>
    !search || c.item_name?.toLowerCase().includes(search.toLowerCase()) || c.vendor_name?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6 max-w-6xl" data-testid="vendor-management">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-extrabold font-[Manrope] text-[#3A322C]">Vendor Management</h1>
          <p className="text-sm text-[#8A7E73] mt-0.5">Suppliers, rate contracts, and quote comparisons</p>
        </div>
        {activeTab === "vendors" && (
          <button onClick={() => { setForm({ category: "general" }); setModal({ type: "vendor", mode: "create" }); setError(""); }}
            className="bg-[#E8956D] text-[#3A322C] rounded-lg px-4 py-2 font-bold text-sm flex items-center gap-1.5">
            <Plus size={14} /> Add Vendor
          </button>
        )}
        {activeTab === "contracts" && (
          <button onClick={() => { setForm({ valid_from: new Date().toISOString().split("T")[0], valid_to: "2099-12-31" }); setModal({ type: "contract", mode: "create" }); setError(""); }}
            className="bg-[#E8956D] text-[#3A322C] rounded-lg px-4 py-2 font-bold text-sm flex items-center gap-1.5">
            <Plus size={14} /> Add Rate Contract
          </button>
        )}
        {activeTab === "quotes" && (
          <button onClick={() => { setForm({}); setSelectedItems([]); setSelectedVendors([]); setModal({ type: "quote-request" }); setError(""); }}
            className="bg-[#E8956D] text-[#3A322C] rounded-lg px-4 py-2 font-bold text-sm flex items-center gap-1.5">
            <Plus size={14} /> New Quote Request
          </button>
        )}
      </div>

      {success && <div className="bg-[#EDF2EE] border border-[#637C64]/30 rounded-lg px-4 py-2.5 text-sm text-[#637C64]">{success}</div>}

      <div className="flex border-b border-[#E5E1DB] overflow-x-auto">
        {TABS.filter(t => !t.subKey || user?.is_admin || user?.permissions?.[`sub_${t.subKey}_view`] !== false).map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            className={`px-4 py-3 text-sm font-semibold flex items-center gap-2 whitespace-nowrap transition-colors -mb-px ${activeTab === t.id ? "border-b-2 border-[#E8956D] text-[#E8956D]" : "text-[#8A7E73] hover:text-[#5C5147]"}`}>
            <t.icon size={15} />{t.label}
          </button>
        ))}
      </div>

      {/* Search + Filters */}
      <div className="flex gap-3 flex-wrap items-center">
        <div className="relative flex-1 min-w-48">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A7E73]" />
          <input placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)}
            className="w-full border border-[#E5E1DB] rounded-lg pl-8 pr-3 py-2 text-sm outline-none focus:border-[#E8956D]" />
        </div>
        {activeTab === "vendors" && (
          <select value={filterCategory} onChange={e => setFilterCategory(e.target.value)}
            className="border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D]">
            <option value="">All Categories</option>
            {CATEGORIES.map(c => <option key={c} value={c} className="capitalize">{c}</option>)}
          </select>
        )}
        {activeTab === "vendors" && (
          <button onClick={() => exportCSV(vendors.map(v => ({ code: v.vendor_code, name: v.name, category: v.category, contact: v.contact_person, phone: v.phone, email: v.email, gst: v.gst_number })), "vendors")}
            className="flex items-center gap-1.5 text-sm border border-[#E5E1DB] rounded-lg px-3 py-2 text-[#5C5147] hover:bg-[#F4F3F0]">
            <Download size={14} /> Export
          </button>
        )}
      </div>

      {loading ? <div className="flex justify-center py-12"><Loader className="animate-spin text-[#E8956D]" /></div> : (
        <>
          {/* VENDORS LIST */}
          {activeTab === "vendors" && (
            <div className="bg-white border border-[#E5E1DB] rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#F4F3F0]">
                    <tr>{["Code", "Name", "Category", "Contact", "Email", "GST", "Contracts", "Rating", ""].map(h => (
                      <th key={h} className="text-left px-3 py-3 text-xs font-bold uppercase tracking-wider text-[#5C5147] whitespace-nowrap">{h}</th>
                    ))}</tr>
                  </thead>
                  <tbody className="divide-y divide-[#F4F3F0]">
                    {vendors.map(v => (
                      <tr key={v.id} className="hover:bg-[#FAFAF8]">
                        <td className="px-3 py-3 font-bold text-[#E8956D]">{v.vendor_code}</td>
                        <td className="px-3 py-3 font-medium text-[#3A322C]">
                          {v.name} {v.is_preferred && <span className="text-[10px] bg-[#E8956D]/10 text-[#E8956D] px-1.5 py-0.5 rounded-full ml-1">Preferred</span>}
                        </td>
                        <td className="px-3 py-3"><span className="text-xs bg-[#F4F3F0] text-[#5C5147] px-2 py-0.5 rounded-full capitalize">{v.category}</span></td>
                        <td className="px-3 py-3 text-[#8A7E73]">{v.contact_person || "—"}</td>
                        <td className="px-3 py-3 text-[#8A7E73]">{v.email || "—"}</td>
                        <td className="px-3 py-3 text-[#8A7E73]">{v.gst_number || "—"}</td>
                        <td className="px-3 py-3"><span className="font-bold text-[#637C64]">{v.contract_count || 0}</span></td>
                        <td className="px-3 py-3"><StarRating value={v.rating || 0} onChange={async (r) => { await vendorAPI.updateRating(v.id, r); fetchTab(); }} /></td>
                        <td className="px-3 py-3"><div className="flex gap-2">
                          <button onClick={() => { setForm({ ...v }); setModal({ type: "vendor", mode: "edit", id: v.id }); setError(""); }} className="text-[#8A7E73] hover:text-[#E8956D]"><Edit size={14} /></button>
                          <button onClick={async () => { if (!await confirm("Deactivate?")) return; await vendorAPI.deleteVendor(v.id); fetchTab(); }} className="text-[#8A7E73] hover:text-[#C2624E]"><Trash2 size={14} /></button>
                        </div></td>
                      </tr>
                    ))}
                    {vendors.length === 0 && <tr><td colSpan={9} className="text-center py-10 text-[#8A7E73]">No vendors found. Add your first supplier.</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* RATE CONTRACTS */}
          {activeTab === "contracts" && (
            <div className="bg-white border border-[#E5E1DB] rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#F4F3F0]">
                    <tr>{["Item", "Vendor", "Rate (₹)", "Unit", "Valid From", "Valid To", "Status", ""].map(h => (
                      <th key={h} className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-[#5C5147]">{h}</th>
                    ))}</tr>
                  </thead>
                  <tbody className="divide-y divide-[#F4F3F0]">
                    {filteredContracts.map(c => {
                      const today = new Date().toISOString().split("T")[0];
                      const isExpired = c.valid_to < today;
                      const isActive = !isExpired && c.is_active;
                      return (
                        <tr key={c.id} className={`hover:bg-[#FAFAF8] ${isExpired ? "opacity-60" : ""}`}>
                          <td className="px-4 py-3 font-medium text-[#3A322C]">{c.item_name}</td>
                          <td className="px-4 py-3 text-[#5C5147]">{c.vendor_name}</td>
                          <td className="px-4 py-3 font-bold text-[#E8956D]">₹{c.agreed_rate}</td>
                          <td className="px-4 py-3 text-[#8A7E73]">{c.unit}</td>
                          <td className="px-4 py-3 text-[#8A7E73]">{c.valid_from}</td>
                          <td className="px-4 py-3 text-[#8A7E73]">{c.valid_to}</td>
                          <td className="px-4 py-3">
                            <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${isActive ? "bg-[#EDF2EE] text-[#637C64]" : "bg-[#FBEAE7] text-[#C2624E]"}`}>
                              {isExpired ? "Expired" : "Active"}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <button onClick={() => { setForm({ ...c }); setModal({ type: "contract", mode: "edit", id: c.id }); setError(""); }} className="text-[#8A7E73] hover:text-[#E8956D]"><Edit size={14} /></button>
                          </td>
                        </tr>
                      );
                    })}
                    {filteredContracts.length === 0 && <tr><td colSpan={8} className="text-center py-10 text-[#8A7E73]">No rate contracts found</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* QUOTE REQUESTS */}
          {activeTab === "quotes" && (
            <div className="space-y-3">
              {gmailPlaceholder}
              {quoteRequests.map(req => (
                <div key={req.id} className="bg-white border border-[#E5E1DB] rounded-xl p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-bold text-[#3A322C]">Quote Request #{req.id?.slice(-6)}</p>
                      <p className="text-xs text-[#8A7E73] mt-0.5">
                        {req.items?.length || 0} items · {req.vendors?.length || 0} vendors · {req.response_count || 0} responses
                        · {fmtDate(req.created_at)}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs font-bold px-2 py-0.5 rounded-full capitalize ${req.status === "received" ? "bg-[#EDF2EE] text-[#637C64]" : req.status === "sent" ? "bg-blue-50 text-blue-700" : "bg-[#F4F3F0] text-[#5C5147]"}`}>
                        {req.status}
                      </span>
                      <button onClick={() => { setSelectedRequest(req); setQuoteResponseForm({ vendor_id: "", items: req.items?.map(i => ({ item_id: i.item_id, item_name: i.item_name, unit: i.unit, rate: 0 })) || [] }); setModal({ type: "add-response" }); setError(""); }}
                        className="text-xs bg-[#E8956D]/10 text-[#E8956D] px-2 py-1 rounded font-bold hover:bg-[#E8956D]/20">+ Add Response</button>
                      <button onClick={() => { setActiveTab("comparison"); loadComparison(req); }}
                        className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded font-bold hover:bg-blue-100">Compare</button>
                    </div>
                  </div>
                  {req.items?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {req.items.slice(0, 5).map((item, i) => (
                        <span key={item.item_name || i} className="text-xs bg-[#F4F3F0] text-[#5C5147] px-2 py-0.5 rounded-full">{item.item_name}</span>
                      ))}
                      {req.items.length > 5 && <span className="text-xs text-[#8A7E73]">+{req.items.length - 5} more</span>}
                    </div>
                  )}
                </div>
              ))}
              {quoteRequests.length === 0 && <div className="text-center py-16 text-[#8A7E73]"><Mail size={48} className="mx-auto mb-3 opacity-20" /><p>No quote requests yet</p></div>}
            </div>
          )}

          {/* QUOTE COMPARISON */}
          {activeTab === "comparison" && (
            <div className="space-y-4">
              {gmailPlaceholder}
              {/* Select request */}
              <div className="flex gap-3 flex-wrap">
                {quoteRequests.map(req => (
                  <button key={req.id} onClick={() => loadComparison(req)}
                    className={`text-sm px-4 py-2 rounded-lg border-2 transition-all ${selectedRequest?.id === req.id ? "border-[#E8956D] bg-[#E8956D]/10 text-[#E8956D]" : "border-[#E5E1DB] text-[#5C5147]"}`}>
                    Request #{req.id?.slice(-6)} ({req.response_count || 0} responses)
                  </button>
                ))}
              </div>
              {comparison && (
                <div className="bg-white border border-[#E5E1DB] rounded-xl overflow-hidden">
                  <div className="px-5 py-4 border-b border-[#F4F3F0] bg-[#FAFAF8]">
                    <h3 className="font-bold text-[#3A322C]">Quote Comparison — {comparison.response_count} vendor{comparison.response_count !== 1 ? "s" : ""} responded</h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-[#F4F3F0]">
                        <tr>
                          <th className="text-left px-4 py-3 text-xs font-bold uppercase text-[#5C5147]">Item</th>
                          <th className="text-left px-4 py-3 text-xs font-bold uppercase text-[#5C5147]">Unit</th>
                          {Object.keys(Object.values(comparison.comparison || {})[0]?.vendors || {}).map(vendor => (
                            <th key={vendor} className="text-center px-4 py-3 text-xs font-bold uppercase text-[#5C5147]">{vendor}</th>
                          ))}
                          <th className="text-center px-4 py-3 text-xs font-bold uppercase text-[#637C64]">Best</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[#F4F3F0]">
                        {Object.entries(comparison.comparison || {}).map(([item, data]) => {
                          const vendors = Object.keys(data.vendors || {});
                          const bestVendor = data.best_vendor;
                          return (
                            <tr key={item} className="hover:bg-[#FAFAF8]">
                              <td className="px-4 py-3 font-medium text-[#3A322C]">{item}</td>
                              <td className="px-4 py-3 text-[#8A7E73]">{data.unit}</td>
                              {vendors.map(vendor => {
                                const vd = data.vendors[vendor];
                                const isBest = vendor === bestVendor;
                                return (
                                  <td key={vendor} className={`px-4 py-3 text-center font-bold ${isBest ? "text-[#637C64] bg-[#EDF2EE]" : "text-[#5C5147]"}`}>
                                    ₹{vd.rate || "—"}
                                  </td>
                                );
                              })}
                              <td className="px-4 py-3 text-center">
                                <span className="text-xs font-bold bg-[#637C64] text-white px-2 py-0.5 rounded-full">{bestVendor}</span>
                              </td>
                            </tr>
                          );
                        })}
                        {Object.keys(comparison.comparison || {}).length === 0 && (
                          <tr><td colSpan={10} className="text-center py-10 text-[#8A7E73]">No responses yet — add vendor responses to see comparison</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* ─── SCORECARD TAB ─── */}
      {activeTab === "scorecard" && (
        <SupplierScorecard />
      )}

      {/* ─── GMAIL TAB ─── */}
      {activeTab === "gmail" && (
        <div className="space-y-5">

          {/* Connection Status Card */}
          <div className={`rounded-2xl p-5 border-2 flex items-center justify-between gap-4 flex-wrap ${gmailStatus.connected ? "border-[#34A853]/40 bg-[#34A853]/5" : "border-[#E5E1DB] bg-white"}`}>
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${gmailStatus.connected ? "bg-[#34A853]/10" : "bg-[#F4F3F0]"}`}>
                <Mail size={24} className={gmailStatus.connected ? "text-[#34A853]" : "text-[#8A7E73]"} />
              </div>
              <div>
                <p className="font-bold text-[#3A322C]">
                  {gmailStatus.connected ? "Gmail Connected" : "Gmail Not Connected"}
                </p>
                <p className="text-sm text-[#8A7E73]">
                  {gmailStatus.connected ? gmailStatus.email : "Connect a Gmail account to send RFQ emails and receive vendor quotes automatically"}
                </p>
              </div>
            </div>
            <div className="flex gap-3">
              {gmailStatus.connected ? (
                <>
                  <button onClick={handleFetchReplies} disabled={gmailLoading}
                    className="flex items-center gap-2 bg-[#4285F4] text-white rounded-lg px-4 py-2.5 text-sm font-bold disabled:opacity-60"
                    data-testid="fetch-replies-btn">
                    {gmailLoading ? <Loader size={16} className="animate-spin" /> : <Inbox size={16} />}
                    Check Inbox for Quotes
                  </button>
                  <button onClick={handleGmailDisconnect} className="border border-[#E5E1DB] text-[#8A7E73] rounded-lg px-3 py-2.5 text-sm font-bold hover:bg-[#F4F3F0]">
                    Disconnect
                  </button>
                </>
              ) : (
                <button onClick={handleGmailConnect} disabled={gmailLoading}
                  className="flex items-center gap-2 bg-[#4285F4] text-white rounded-lg px-5 py-2.5 text-sm font-bold disabled:opacity-60"
                  data-testid="connect-gmail-btn">
                  {gmailLoading ? <Loader size={16} className="animate-spin" /> : <ExternalLink size={16} />}
                  Connect Gmail
                </button>
              )}
            </div>
          </div>

          {/* Status message */}
          {gmailMsg && (
            <div className={`rounded-lg px-4 py-3 text-sm font-medium ${gmailMsg.includes("Failed") || gmailMsg.includes("error") ? "bg-[#FBEAE7] text-[#C2624E]" : "bg-[#EDF2EE] text-[#637C64]"}`}>
              {gmailMsg}
            </div>
          )}

          {gmailStatus.connected && (
            <>
              {/* Send RFQ section */}
              <div className="bg-white border border-[#E5E1DB] rounded-xl overflow-hidden">
                <div className="px-5 py-4 border-b border-[#F4F3F0] flex items-center justify-between">
                  <h3 className="font-bold text-[#3A322C]">Send RFQ Emails</h3>
                  <span className="text-xs text-[#8A7E73]">Quote requests with vendor emails</span>
                </div>
                {quoteRequests.length === 0 ? (
                  <div className="p-8 text-center text-[#8A7E73] text-sm">No quote requests yet. Create one in the Quote Requests tab first.</div>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="bg-[#F4F3F0]">
                      <tr>{["Request", "Items", "Vendors", "Status", "Action"].map(h => (
                        <th key={h} className="text-left px-4 py-3 text-xs font-bold uppercase text-[#5C5147]">{h}</th>
                      ))}</tr>
                    </thead>
                    <tbody className="divide-y divide-[#F4F3F0]">
                      {quoteRequests.map(req => (
                        <tr key={req.id} className="hover:bg-[#FAFAF8]">
                          <td className="px-4 py-3 font-mono text-xs text-[#8A7E73]">#{req.id?.slice(-6)}</td>
                          <td className="px-4 py-3 text-[#3A322C]">{(req.items || []).map(i => i.item_name).join(", ").slice(0, 40) || "—"}</td>
                          <td className="px-4 py-3 text-[#5C5147]">{(req.vendors || []).length} vendor(s)</td>
                          <td className="px-4 py-3">
                            <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${req.status === "sent" ? "bg-blue-100 text-blue-700" : req.status === "received" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"}`}>
                              {req.status}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <button onClick={() => { setSendRFQModal(req); setRfqMessage(""); }}
                              className="flex items-center gap-1 text-xs bg-[#4285F4]/10 text-[#4285F4] px-3 py-1.5 rounded-lg font-bold hover:bg-[#4285F4]/20"
                              data-testid={`send-rfq-${req.id}`}>
                              <Send size={12} /> Send via Gmail
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              {/* Pending Review Queue */}
              <div className="bg-white border border-[#E5E1DB] rounded-xl overflow-hidden">
                <div className="px-5 py-4 border-b border-[#F4F3F0] flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <h3 className="font-bold text-[#3A322C]">Incoming Quote Replies</h3>
                    {pendingQuotes.filter(p => p.status === "pending_review").length > 0 && (
                      <span className="bg-[#E8956D] text-white text-xs font-bold px-2 py-0.5 rounded-full">
                        {pendingQuotes.filter(p => p.status === "pending_review").length} pending
                      </span>
                    )}
                  </div>
                  <div className="flex gap-2">
                    {["pending_review", "approved", "rejected"].map(s => (
                      <button key={s} onClick={() => setPendingFilter(s)}
                        className={`text-xs px-3 py-1.5 rounded-lg font-bold capitalize transition-colors ${pendingFilter === s ? "bg-[#E8956D] text-[#3A322C]" : "border border-[#E5E1DB] text-[#8A7E73]"}`}>
                        {s.replace("_", " ")}
                      </button>
                    ))}
                  </div>
                </div>

                {pendingQuotes.filter(p => !pendingFilter || p.status === pendingFilter).length === 0 ? (
                  <div className="p-8 text-center text-[#8A7E73] text-sm">
                    {pendingFilter === "pending_review"
                      ? "No pending quotes. Click \"Check Inbox for Quotes\" to scan for new vendor replies."
                      : `No ${pendingFilter} quotes.`}
                  </div>
                ) : (
                  <div className="divide-y divide-[#F4F3F0]">
                    {pendingQuotes.filter(p => !pendingFilter || p.status === pendingFilter).map(p => (
                      <div key={p.id} className="p-5" data-testid={`pending-quote-${p.id}`}>
                        <div className="flex items-start justify-between gap-3 flex-wrap">
                          <div>
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="font-bold text-[#3A322C]">{p.vendor_name}</p>
                              <span className="text-xs font-mono text-[#8A7E73]">{p.from_email}</span>
                              {p.status === "approved" && <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-bold">Approved</span>}
                              {p.status === "rejected" && <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-bold">Rejected</span>}
                            </div>
                            <p className="text-xs text-[#8A7E73] mt-0.5">{p.subject} · {p.date}</p>
                          </div>
                          {p.status === "pending_review" && (
                            <div className="flex gap-2">
                              <button onClick={() => handleApproveQuote(p)}
                                className="flex items-center gap-1 text-xs bg-[#637C64] text-white px-3 py-1.5 rounded-lg font-bold"
                                data-testid={`approve-quote-${p.id}`}>
                                <CheckCircle size={12} /> Approve
                              </button>
                              <button onClick={() => handleRejectQuote(p)}
                                className="flex items-center gap-1 text-xs border border-[#C2624E] text-[#C2624E] px-3 py-1.5 rounded-lg font-bold"
                                data-testid={`reject-quote-${p.id}`}>
                                <XCircle size={12} /> Reject
                              </button>
                            </div>
                          )}
                        </div>

                        {/* AI Parsed Data */}
                        {p.parsed_data?.items?.length > 0 && (
                          <div className="mt-3 bg-[#F4F3F0] rounded-lg p-3">
                            <p className="text-xs font-bold uppercase text-[#8A7E73] mb-2">AI Parsed Quote Data</p>
                            <div className="flex flex-wrap gap-2">
                              {p.parsed_data.items.map((item, i) => (
                                <div key={item.item_name || i} className="bg-white rounded-lg px-3 py-2 text-sm border border-[#E5E1DB]">
                                  <p className="font-bold text-[#3A322C]">{item.item_name}</p>
                                  <p className="text-[#637C64] font-bold">₹{item.rate} / {item.unit}</p>
                                  {item.notes && <p className="text-xs text-[#8A7E73]">{item.notes}</p>}
                                </div>
                              ))}
                            </div>
                            {p.parsed_data.general_notes && (
                              <p className="text-xs text-[#8A7E73] mt-2">{p.parsed_data.general_notes}</p>
                            )}
                          </div>
                        )}
                        {!p.parsed_data?.items?.length && (
                          <div className="mt-2 text-xs text-[#C2624E] bg-[#FBEAE7] px-3 py-2 rounded-lg">
                            Could not extract quote data from this email. You can review the email content manually.
                          </div>
                        )}

                        {/* Email snippet */}
                        <details className="mt-2">
                          <summary className="text-xs text-[#8A7E73] cursor-pointer hover:text-[#5C5147]">View email content</summary>
                          <pre className="mt-2 text-xs text-[#5C5147] bg-[#FAFAF8] border border-[#E5E1DB] rounded-lg p-3 overflow-x-auto whitespace-pre-wrap max-h-40">{p.email_body}</pre>
                        </details>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {/* MODALS */}
      {/* Vendor form */}
      {modal?.type === "vendor" && (
        <Modal title={`${modal.mode === "create" ? "Add" : "Edit"} Vendor`} onClose={() => setModal(null)} wide>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Vendor Name *</label><input className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.name || ""} onChange={e => setForm({ ...form, name: e.target.value })} /></div>
            <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Category *</label>
              <select className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.category || "general"} onChange={e => setForm({ ...form, category: e.target.value })}>
                {CATEGORIES.map(c => <option key={c} value={c} className="capitalize">{c}</option>)}
              </select></div>
            <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Contact Person</label><input className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.contact_person || ""} onChange={e => setForm({ ...form, contact_person: e.target.value })} /></div>
            <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Phone</label><input className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.phone || ""} onChange={e => setForm({ ...form, phone: e.target.value })} /></div>
            <div className="col-span-2"><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Email (for quote requests)</label><input type="email" className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.email || ""} onChange={e => setForm({ ...form, email: e.target.value })} /></div>
            <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">GST Number</label><input className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.gst_number || ""} onChange={e => setForm({ ...form, gst_number: e.target.value })} /></div>
            <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">PAN Number</label><input className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.pan_number || ""} onChange={e => setForm({ ...form, pan_number: e.target.value })} /></div>
            <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Bank Name</label><input className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.bank_name || ""} onChange={e => setForm({ ...form, bank_name: e.target.value })} /></div>
            <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">IFSC Code</label><input className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.ifsc_code || ""} onChange={e => setForm({ ...form, ifsc_code: e.target.value })} /></div>
            <div className="col-span-2"><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Address</label><textarea rows={2} className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D] resize-none" value={form.address || ""} onChange={e => setForm({ ...form, address: e.target.value })} /></div>
            <div className="col-span-2">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={form.is_preferred || false} onChange={e => setForm({ ...form, is_preferred: e.target.checked })} />
                <span className="font-semibold text-[#E8956D]">Mark as Preferred Vendor</span>
              </label>
            </div>
          </div>
          {error && <p className="mt-4 text-sm text-[#C2624E] bg-[#FBEAE7] px-3 py-2 rounded-lg">{error}</p>}
          <div className="flex gap-3 mt-5">
            <button onClick={() => setModal(null)} className="flex-1 border border-[#E5E1DB] rounded-lg py-2.5 text-sm font-bold text-[#5C5147]">Cancel</button>
            <button onClick={handleSaveVendor} className="flex-1 bg-[#E8956D] rounded-lg py-2.5 text-sm font-bold text-[#3A322C]">Save Vendor</button>
          </div>
        </Modal>
      )}

      {/* Rate contract form */}
      {modal?.type === "contract" && (
        <Modal title={`${modal.mode === "create" ? "Add" : "Edit"} Rate Contract`} onClose={() => setModal(null)}>
          <div className="space-y-4">
            <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Vendor *</label>
              <select className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.vendor_id || ""} onChange={e => setForm({ ...form, vendor_id: e.target.value })}>
                <option value="">Select vendor</option>
                {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
              </select></div>
            <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Item *</label>
              <select className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.item_id || ""} onChange={e => setForm({ ...form, item_id: e.target.value })}>
                <option value="">Select item</option>
                {masterItems.map(i => <option key={i.id} value={i.id}>{i.name} ({i.unit})</option>)}
              </select></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Agreed Rate (₹) *</label><input type="number" step="0.01" className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.agreed_rate || ""} onChange={e => setForm({ ...form, agreed_rate: parseFloat(e.target.value) })} /></div>
              <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Valid From</label><input type="date" className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.valid_from || ""} onChange={e => setForm({ ...form, valid_from: e.target.value })} /></div>
              <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Valid To</label><input type="date" className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.valid_to || ""} onChange={e => setForm({ ...form, valid_to: e.target.value })} /></div>
            </div>
            <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Notes</label><input className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.notes || ""} onChange={e => setForm({ ...form, notes: e.target.value })} /></div>
            {error && <p className="text-sm text-[#C2624E] bg-[#FBEAE7] px-3 py-2 rounded-lg">{error}</p>}
            <div className="flex gap-3">
              <button onClick={() => setModal(null)} className="flex-1 border border-[#E5E1DB] rounded-lg py-2.5 text-sm font-bold text-[#5C5147]">Cancel</button>
              <button onClick={handleSaveContract} className="flex-1 bg-[#E8956D] rounded-lg py-2.5 text-sm font-bold text-[#3A322C]">Save Contract</button>
            </div>
          </div>
        </Modal>
      )}

      {/* Quote request form */}
      {modal?.type === "quote-request" && (
        <Modal title="New Quote Request" onClose={() => setModal(null)} wide>
          <div className="space-y-4">
            <div>
              <label className="text-xs font-bold uppercase text-[#8A7E73] mb-2 block">Select Items to Quote *</label>
              <div className="max-h-48 overflow-y-auto border border-[#E5E1DB] rounded-lg p-2 space-y-1">
                {masterItems.map(i => (
                  <label key={i.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-[#FAFAF8] px-2 py-1 rounded">
                    <input type="checkbox" checked={selectedItems.includes(i.id)}
                      onChange={e => setSelectedItems(prev => e.target.checked ? [...prev, i.id] : prev.filter(x => x !== i.id))} />
                    {i.name} ({i.unit})
                  </label>
                ))}
              </div>
              <p className="text-xs text-[#8A7E73] mt-1">{selectedItems.length} items selected</p>
            </div>
            <div>
              <label className="text-xs font-bold uppercase text-[#8A7E73] mb-2 block">Select Vendors *</label>
              <div className="max-h-36 overflow-y-auto border border-[#E5E1DB] rounded-lg p-2 space-y-1">
                {vendors.map(v => (
                  <label key={v.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-[#FAFAF8] px-2 py-1 rounded">
                    <input type="checkbox" checked={selectedVendors.includes(v.id)}
                      onChange={e => setSelectedVendors(prev => e.target.checked ? [...prev, v.id] : prev.filter(x => x !== v.id))} />
                    {v.name} {v.email && <span className="text-[#8A7E73] text-xs">({v.email})</span>}
                  </label>
                ))}
              </div>
              <p className="text-xs text-[#8A7E73] mt-1">{selectedVendors.length} vendors selected</p>
            </div>
            <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Message to Vendors</label>
              <textarea rows={3} className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D] resize-none"
                placeholder="Please share your best rates for the listed items..." value={form.message || ""} onChange={e => setForm({ ...form, message: e.target.value })} /></div>
            <div className="bg-[#FDF4EB] border border-[#D98032]/30 rounded-lg px-4 py-3 text-xs text-[#D98032]">
              Gmail not connected yet — emails will be sent automatically once connected tomorrow. You can still record responses manually.
            </div>
            {error && <p className="text-sm text-[#C2624E] bg-[#FBEAE7] px-3 py-2 rounded-lg">{error}</p>}
            <div className="flex gap-3">
              <button onClick={() => setModal(null)} className="flex-1 border border-[#E5E1DB] rounded-lg py-2.5 text-sm font-bold text-[#5C5147]">Cancel</button>
              <button onClick={handleCreateQuoteRequest} disabled={!selectedItems.length || !selectedVendors.length}
                className="flex-1 bg-[#E8956D] rounded-lg py-2.5 text-sm font-bold text-[#3A322C] disabled:opacity-50">Create Request</button>
            </div>
          </div>
        </Modal>
      )}

      {/* Add quote response */}
      {modal?.type === "add-response" && selectedRequest && (
        <Modal title={`Add Quote Response — Request #${selectedRequest.id?.slice(-6)}`} onClose={() => setModal(null)}>
          <div className="space-y-4">
            <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Vendor *</label>
              <select className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={quoteResponseForm.vendor_id} onChange={e => setQuoteResponseForm({ ...quoteResponseForm, vendor_id: e.target.value })}>
                <option value="">Select vendor who responded</option>
                {(selectedRequest.vendors || []).map(v => <option key={v.vendor_id} value={v.vendor_id}>{v.vendor_name}</option>)}
              </select></div>
            <div>
              <label className="text-xs font-bold uppercase text-[#8A7E73] mb-2 block">Quoted Rates</label>
              <div className="border border-[#E5E1DB] rounded-lg overflow-hidden">
                <div className="grid grid-cols-[1fr_120px] bg-[#F4F3F0] px-4 py-2">
                  <span className="text-xs font-bold uppercase text-[#5C5147]">Item</span>
                  <span className="text-xs font-bold uppercase text-[#5C5147]">Rate (₹)</span>
                </div>
                {quoteResponseForm.items.map((item, i) => (
                  <div key={`vendor-row-${i}`} className="grid grid-cols-[1fr_120px] px-4 py-2.5 border-t border-[#F4F3F0] items-center gap-3">
                    <span className="text-sm text-[#3A322C]">{item.item_name} ({item.unit})</span>
                    <input type="number" step="0.01" placeholder="0.00"
                      className="border border-[#E5E1DB] rounded-lg px-2 py-1.5 text-sm outline-none focus:border-[#E8956D] w-full"
                      value={item.rate || ""}
                      onChange={e => {
                        const items = [...quoteResponseForm.items];
                        items[i] = { ...items[i], rate: parseFloat(e.target.value) || 0 };
                        setQuoteResponseForm({ ...quoteResponseForm, items });
                      }} />
                  </div>
                ))}
              </div>
            </div>
            <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Notes from vendor</label>
              <textarea rows={2} className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D] resize-none" value={quoteResponseForm.notes || ""} onChange={e => setQuoteResponseForm({ ...quoteResponseForm, notes: e.target.value })} /></div>
            {error && <p className="text-sm text-[#C2624E] bg-[#FBEAE7] px-3 py-2 rounded-lg">{error}</p>}
            <div className="flex gap-3">
              <button onClick={() => setModal(null)} className="flex-1 border border-[#E5E1DB] rounded-lg py-2.5 text-sm font-bold text-[#5C5147]">Cancel</button>
              <button onClick={handleAddResponse} className="flex-1 bg-[#E8956D] rounded-lg py-2.5 text-sm font-bold text-[#3A322C]">Save Response</button>
            </div>
          </div>
        </Modal>
      )}
      {ConfirmModal}

      {/* Send RFQ Modal */}
      {sendRFQModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg">
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E1DB]">
              <h2 className="font-bold text-lg text-[#3A322C]">Send RFQ via Gmail</h2>
              <button onClick={() => setSendRFQModal(null)}><X size={20} /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-[#F4F3F0] rounded-lg p-3 text-sm">
                <p className="font-bold text-[#3A322C] mb-1">Will send to:</p>
                {(sendRFQModal.vendors || []).map((v, i) => (
                  <p key={v.email || i} className="text-[#5C5147]">{v.vendor_name} — <span className="font-mono text-xs">{v.email || "No email"}</span></p>
                ))}
              </div>
              <div>
                <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Additional Message (optional)</label>
                <textarea rows={3} className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D] resize-none"
                  placeholder="E.g. Please include GST and delivery charges..."
                  value={rfqMessage} onChange={e => setRfqMessage(e.target.value)} />
              </div>
              <div className="flex gap-3">
                <button onClick={() => setSendRFQModal(null)} className="flex-1 border border-[#E5E1DB] rounded-lg py-2.5 text-sm font-bold text-[#5C5147]">Cancel</button>
                <button onClick={() => handleSendRFQ(sendRFQModal)} disabled={gmailLoading}
                  className="flex-1 bg-[#4285F4] text-white rounded-lg py-2.5 text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-60">
                  {gmailLoading ? <Loader size={16} className="animate-spin" /> : <Send size={16} />}
                  Send Emails
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
